import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getClients } from '../services/clients';
import { getAllUsers } from '../services/users';
import { getTaskTypes } from '../services/taskTypes';
import './TaskHistory.css';

const CALENDAR_TABLE = 'calendar_events';

async function fetchCalendarEventsFiltered(filters = {}) {
  let query = supabase
    .from(CALENDAR_TABLE)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .order('start_date', { ascending: false });
  if (filters.clientId) query = query.eq('client_id', filters.clientId);
  if (filters.taskType) query = query.eq('task_type', filters.taskType);
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.startDate) query = query.gte('start_date', filters.startDate.toISOString());
  if (filters.endDate) query = query.lte('start_date', filters.endDate.toISOString());
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'started', label: 'Started' },
  { value: 'completed', label: 'Completed' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { dateStyle: 'short' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

export default function TaskHistory() {
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: '',
    status: '',
    taskType: '',
    startDate: '',
    endDate: '',
  });
  const [taskTypes, setTaskTypes] = useState([]);

  const userMap = React.useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.id] = u.user_metadata?.full_name || u.email || '—'; });
    return m;
  }, [users]);

  const loadClients = useCallback(async () => {
    try {
      const data = await getClients();
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadTaskTypes = useCallback(async () => {
    try {
      const data = await getTaskTypes(false);
      setTaskTypes(data || []);
    } catch (error) {
      console.error('Error loading task types:', error);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const opts = {};
      if (filters.clientId) opts.clientId = filters.clientId;
      if (filters.status) opts.status = filters.status;
      if (filters.taskType) opts.taskType = filters.taskType;
      if (filters.startDate) opts.startDate = new Date(filters.startDate + 'T00:00:00');
      if (filters.endDate) opts.endDate = new Date(filters.endDate + 'T23:59:59');
      const data = await fetchCalendarEventsFiltered(opts);
      setEvents(data);
    } catch (error) {
      console.error('Error loading task history:', error);
      alert('Failed to load task history: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [filters.clientId, filters.status, filters.taskType, filters.startDate, filters.endDate]);

  useEffect(() => {
    loadClients();
    loadUsers();
    loadTaskTypes();
  }, [loadClients, loadUsers, loadTaskTypes]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="task-history-page">
      <div className="task-history-header">
        <h1>Task History</h1>
        <p className="task-history-subtitle">View and filter all tasks across the BMS.</p>
      </div>

      <div className="task-history-filters">
        <div className="filter-group">
          <label htmlFor="filter-client">Client</label>
          <select
            id="filter-client"
            value={filters.clientId}
            onChange={(e) => handleFilterChange('clientId', e.target.value)}
          >
            <option value="">All clients</option>
            {(clients || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="filter-task-type">Type</label>
          <select
            id="filter-task-type"
            value={filters.taskType}
            onChange={(e) => handleFilterChange('taskType', e.target.value)}
          >
            <option value="">All types</option>
            {taskTypes.length > 0
              ? taskTypes.map((tt) => (
                  <option key={tt.id} value={tt.code}>
                    {tt.name}
                  </option>
                ))
              : [
                  { code: 'general', name: 'General' },
                  { code: 'call', name: 'Call' },
                  { code: 'follow_up', name: 'Follow-Up' },
                  { code: 'estimate_follow_up', name: 'Estimate Follow-Up' },
                  { code: 'invoice_follow_up', name: 'Invoice Follow-Up' },
                  { code: 'site_visit', name: 'Site Visit' },
                ].map((tt) => (
                  <option key={tt.code} value={tt.code}>
                    {tt.name}
                  </option>
                ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="filter-start">From date</label>
          <input
            id="filter-start"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="filter-end">To date</label>
          <input
            id="filter-end"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      <div className="task-history-content">
        {loading ? (
          <div className="task-history-loading">
            <div className="loading-spinner"></div>
            <p>Loading tasks…</p>
          </div>
        ) : events.length === 0 ? (
          <div className="task-history-empty">
            <p>No tasks match the current filters.</p>
          </div>
        ) : (
          <div className="task-history-table-wrap">
            <table className="task-history-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Client</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Assigned to</th>
                  <th>Note / Order</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id}>
                    <td>
                      {ev.all_day
                        ? formatDate(ev.start_date)
                        : formatDateTime(ev.start_date)}
                    </td>
                    <td>
                      {ev.clients ? (
                        <Link to={`/clients/${ev.client_id}`} className="client-link">
                          {ev.clients.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{ev.title || '—'}</td>
                    <td className="task-type-cell">
                      {(ev.task_type || 'general').replace(/_/g, ' ')}
                    </td>
                    <td>
                      <span className={`status-badge status-${ev.status || 'scheduled'}`}>
                        {ev.status || 'scheduled'}
                      </span>
                    </td>
                    <td>{ev.assigned_to ? userMap[ev.assigned_to] || '—' : '—'}</td>
                    <td className="task-history-links-cell">
                      {ev.client_id ? (
                        <>
                          <Link to={`/clients/${ev.client_id}?tab=order-form`} className="task-history-link">
                            Order form
                          </Link>
                          {ev.completion_note?.trim() && (
                            <>
                              {' · '}
                              <Link to={`/clients/${ev.client_id}?tab=order-form`} className="task-history-link">
                                View note
                              </Link>
                            </>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
